import { useCallback } from "react";
import { useFormContext } from "@jsq/ds/forms";
import { hasBankInfo } from "../common/utils";
import { defaultBankAddress } from "../Components/BankValidators/constants";
import { type RoutingNumberInputFields } from "../Components/BankValidators/RoutingNumberInput/constants";
import { type BankAddress } from "../Components/BankValidators/types";
import { type BankDetailsInputFields } from "../Components/WirePaymentPrefInput/BankDetails/constants";
import { type WirePaymentPrefInputType } from "../Components/WirePaymentPrefInput/constants";
import { useCachedCountryNameMap } from "./use-cached-country-name-map";

export type HandleValidateParams = {
  setShowBankValidatorError: (show: boolean) => void;
  bankName: string | null;
  bankAddress: BankAddress | null;
  isValid: boolean;
};

export type UseBankValidationParams =
  | {
      validatedCountryField?: BankDetailsInputFields.BankCountryValidatedCountry;
      nameField: BankDetailsInputFields.BankName;
      addressField?: BankDetailsInputFields.BankAddress;
    }
  | {
      validatedCountryField?: BankDetailsInputFields.IntermediaryBankCountryValidatedCountry;
      nameField: BankDetailsInputFields.IntermediaryBankName;
      addressField?: BankDetailsInputFields.IntermediaryBankAddress;
    }
  | {
      validatedCountryField?: undefined;
      nameField: RoutingNumberInputFields.BankName;
      addressField?: undefined;
    };

interface UseBankValidationReturn {
  handleClear: () => void;
  handleValidateCompleted: ({
    setShowBankValidatorError,
    bankName,
    bankAddress,
    isValid,
  }: HandleValidateParams) => void;
}

export const useBankValidation = ({
  validatedCountryField,
  nameField,
  addressField,
}: UseBankValidationParams): UseBankValidationReturn => {
  const { values, setFieldValue } = useFormContext<WirePaymentPrefInputType>();
  const countryNameMap = useCachedCountryNameMap();

  // TODO should this not set shouldValidate?
  const handleClear = useCallback(() => {
    if (validatedCountryField) {
      void setFieldValue(validatedCountryField, "", false);
    }
    if (addressField) {
      void setFieldValue(addressField, defaultBankAddress, false);
    }

    void setFieldValue(nameField, "", false);
  }, [setFieldValue, validatedCountryField, nameField, addressField]);

  const handleValidateCompleted = useCallback(
    ({ setShowBankValidatorError, bankName, bankAddress, isValid }: HandleValidateParams) => {
      // Merge the provided bank address with default values
      const bankAddressWithDefaults: BankAddress = {
        country: bankAddress?.country || defaultBankAddress.country,
        line1: bankAddress?.line1 || defaultBankAddress.line1,
        line2: bankAddress?.line2 || defaultBankAddress.line2,
        locality: bankAddress?.locality || defaultBankAddress.locality,
        postalCode: bankAddress?.postalCode || defaultBankAddress.postalCode,
        region: bankAddress?.region || defaultBankAddress.region,
      };

      if (isValid) {
        // Update the bank name field
        void setFieldValue(nameField, bankName, false);

        // Reset error state
        setShowBankValidatorError(false);

        // Ensure the country code is set if available
        if (bankAddressWithDefaults?.country && countryNameMap && validatedCountryField) {
          void setFieldValue(
            validatedCountryField,
            countryNameMap?.[bankAddressWithDefaults?.country],
          );
        }

        if (
          values &&
          values[nameField] !== bankName &&
          hasBankInfo(bankAddressWithDefaults) &&
          addressField
        ) {
          // Check if the bank name has changed and if the new address has sufficient info
          void setFieldValue(addressField, bankAddressWithDefaults, false);
        } else if (
          addressField &&
          values &&
          !hasBankInfo(values[addressField]) &&
          hasBankInfo(bankAddressWithDefaults)
        ) {
          // Update the bank address fields if previous address was incomplete and new address has info
          void setFieldValue(addressField, bankAddressWithDefaults, false);
        }
      } else {
        // Show the error message without impacting formik errors. Serves as a warning.
        setShowBankValidatorError(true);
      }
    },
    [countryNameMap, setFieldValue, nameField, validatedCountryField, addressField, values],
  );

  return { handleClear, handleValidateCompleted };
};
